import type {
  InteractiveObject,
  SpawnPoint,
  WalkObstacle,
  WorldSceneryLayer,
} from '@/world-prototype/types';
import {
  placeActivationZone,
  type ActivationZoneDef,
} from '@/world-prototype/objects/activationZone';
import {
  occlusionBackgroundDepth,
  occlusionRectWorld,
  sourceRectToPolygon,
  type OcclusionDef,
} from '@/world-prototype/objects/occlusion';
import {
  localOffsetToWorld,
  localOffsetsToWorld,
  normalizeRotationDeg,
} from '@/world-prototype/objects/propTransform';
import {
  isQuadCorners,
  quadContainSize,
  rectQuadCorners,
  rotateQuadCorners,
  type QuadCorners,
} from '@/world-prototype/objects/quadShape';
import type { LayoutProfilePair } from '@/world-prototype/camera/backyardPresentation';

export type { ActivationZoneDef, OcclusionDef };
export type { OcclusionSourceRect } from '@/world-prototype/objects/occlusion';
export type { LayoutProfilePair, PropLayoutTransform } from '@/world-prototype/camera/backyardPresentation';

/**
 * Y-depth sort key shared by scenery props, interactive objects, and the
 * character. One formula — anything standing on the ground sorts by the Y of
 * its ground contact.
 */
export function depthFromY(y: number): number {
  return Math.round(100 + y * 100);
}

/** Transparent object sprite (native pixel size drives aspect-correct layout). */
export interface WorldObjectSprite {
  src: string;
  alt: string;
  /** Native image dimensions in pixels. */
  imageWidth: number;
  imageHeight: number;
}

/**
 * Optional patio-style floor that shares a prop’s ground transform.
 * Painted as a center-anchored ground-decal beneath the character; never a
 * separate World Editor object.
 */
export interface CompositeFloorDef {
  sprite: WorldObjectSprite;
  /**
   * Floor width as a multiple of the parent `widthFrac`.
   * Default 1.
   */
  widthScale?: number;
  /**
   * Optional world-height fraction. When omitted, height follows the sprite
   * aspect at the resolved floor width.
   */
  heightFrac?: number;
  /**
   * Center-anchor offset from the parent ground anchor (bottom-center).
   * Default `{ x: 0, y: 0 }`.
   */
  offset?: SpawnPoint;
  /**
   * Freeform quad (TL→TR→BR→BL) in floor-local (center) space.
   * Same reshape tool as standalone patio ground decals.
   */
  quadCorners?: QuadCorners;
  /**
   * Floor width the `quadCorners` were authored against. When the live floor
   * width differs (parent scale / widthScale), corners are scaled uniformly
   * so D/T/M layouts keep the same relative shape.
   */
  quadAuthoredWidth?: number;
}

/** Visual contents for garden-bed props (raised bed structure stays shared). */
export type GardenType = 'flowers' | 'vegetables';

/** Pick the active sprite for a def (gardenType → spriteVariants → sprite). */
export function resolveObjectSprite(
  def: Pick<WorldObjectDef, 'sprite' | 'gardenType' | 'spriteVariants'>,
): WorldObjectSprite | undefined {
  const type = def.gardenType ?? 'vegetables';
  return def.spriteVariants?.[type] ?? def.sprite;
}

/**
 * One placeable world object, authored around a single ground anchor
 * (bottom-center of the art, where it touches the ground).
 *
 * All footprint / stand / hit coordinates are OFFSETS from the anchor, so
 * repositioning an object means changing `x`/`y` only.
 */
export interface WorldObjectDef {
  id: string;
  title: string;
  icon: string;
  actionLabel: string;
  /** Placeholder look until final art ships (see InteractiveObject.shape). */
  shape: InteractiveObject['shape'];
  /**
   * Ground anchor in world coords (0–1).
   * Prefer `layouts` for backyard dual presentation; flat fields remain for
   * legacy / resolved defs passed into `placeObject`.
   */
  x: number;
  y: number;
  /** Rendered sprite width as a fraction of world width. */
  widthFrac: number;
  /**
   * Per-presentation visual transforms (desktop / mobile). Shared identity,
   * art, and local geometry stay outside this map.
   */
  layouts?: LayoutProfilePair;
  /**
   * Separate transparent sprite. Omit for objects baked into the master
   * plate (interactivity still works — hotspot/stand/footprint are data).
   */
  sprite?: WorldObjectSprite;
  /**
   * Ground footprint (collision) as offsets from the anchor.
   * Base band only — never the roof/canopy. Empty = no collision.
   */
  footprint: readonly SpawnPoint[];
  /** Where the character stands to interact, as an offset from the anchor. */
  stand: SpawnPoint;
  /**
   * Click hotspot center as an offset from the ground anchor.
   * Must sit on the visible art (not the grass). When omitted and a sprite
   * is present, defaults to the sprite’s visual center.
   */
  hit?: SpawnPoint;
  /** Clickable hotspot radius in world-width units (covers the visible art). */
  hitRadius: number;
  /**
   * Small walk-in activation zone as offsets from the ground anchor.
   * Independent of footprint / sprite bounds — must be authored explicitly.
   */
  activationZone?: ActivationZoneDef;
  /**
   * Partial walk-behind occlusion — only this crop draws in front of the
   * character. Prefer this over a full-silhouette `occluderClip`.
   * Sprite props emit background + occlusion layers from the same texture.
   * Baked props re-paint this crop from the master plate.
   */
  occlusion?: OcclusionDef;
  /**
   * Legacy full-silhouette clip (baked props). Prefer `occlusion` so only the
   * lower band covers the character. Ignored when `occlusion.enabled`.
   */
  occluderClip?: readonly SpawnPoint[];
  /** Override the Y-derived paint depth. */
  depth?: number;
  /**
   * Rotation in degrees around the ground anchor (bottom-center).
   * Positive = clockwise (CSS). Default 0.
   */
  rotationDeg?: number;
  /**
   * Horizontal mirror around the ground anchor. Omitted / false = as authored.
   */
  flipX?: boolean;
  /**
   * Garden visual variant. Omitted / `vegetables` keeps the classic veggie bed.
   * Pair with `spriteVariants.flowers` for a flower-bed look.
   */
  gardenType?: GardenType;
  /**
   * Optional alternate sprites keyed by gardenType. `sprite` remains the
   * default (vegetables) for backward compatibility.
   */
  spriteVariants?: Partial<Record<GardenType, WorldObjectSprite>>;
  /**
   * Optional composite floor (e.g. patio pavers under a roof). Shares this
   * def’s x/y/scale/rotation/flip; emits an internal ground-decal layer the
   * editor never lists separately.
   */
  floor?: CompositeFloorDef;
  /** When false, no click hotspot / object-focus wiring is emitted. */
  interactive?: boolean;
  /** Soft ambient motion for the sprite layer. */
  ambient?: WorldSceneryLayer['ambient'];
  /** Soft grounding shadow under the sprite. */
  groundShadow?: WorldSceneryLayer['groundShadow'];
  /** Toggle without deleting the definition. */
  enabled?: boolean;
}

export interface PlaceObjectOptions {
  /**
   * World layer aspect ratio (width / height, e.g. 1024 / 576).
   * Needed to convert width-fraction sizing into an aspect-correct
   * height fraction — width % and height % are different visual units.
   */
  worldAspect: number;
  /** Master plate src, required when the def declares an occluderClip. */
  masterSrc?: string;
}

export interface PlacedObject {
  /** Sprite and/or occluder layers (empty for baked objects without occluder). */
  sceneryLayers: readonly WorldSceneryLayer[];
  /** Omitted when the def has no usable footprint. */
  walkObstacle: WalkObstacle | null;
  /** Omitted when `interactive: false`. */
  interactiveObject: InteractiveObject | null;
}

/**
 * Aspect-correct height fraction for a sprite rendered at `widthFrac` of
 * world width. Without the world-aspect factor, `background-size: contain`
 * letterboxes the sprite smaller than its declared width.
 */
export function spriteHeightFrac(
  widthFrac: number,
  sprite: Pick<WorldObjectSprite, 'imageWidth' | 'imageHeight'>,
  worldAspect: number,
): number {
  return widthFrac * (sprite.imageHeight / sprite.imageWidth) * worldAspect;
}

/**
 * Default click hotspot for a bottom-anchored sprite: the visual center of
 * the art (halfway up from the ground anchor), not the grass at the feet.
 */
export function defaultSpriteHitOffset(
  widthFrac: number,
  sprite: Pick<WorldObjectSprite, 'imageWidth' | 'imageHeight'>,
  worldAspect: number,
): SpawnPoint {
  const height = spriteHeightFrac(widthFrac, sprite, worldAspect);
  return { x: 0, y: -height * 0.5 };
}

function offsetPolygon(
  anchorX: number,
  anchorY: number,
  offsets: readonly SpawnPoint[],
): SpawnPoint[] {
  return offsets.map((p) => ({ x: anchorX + p.x, y: anchorY + p.y }));
}

/** World stand / spawn point for a resolved object def (anchor + local stand). */
export function standWorldFromDef(
  def: Pick<
    WorldObjectDef,
    'x' | 'y' | 'stand' | 'rotationDeg' | 'flipX'
  >,
  worldAspect: number,
): SpawnPoint {
  const rotationDeg = normalizeRotationDeg(def.rotationDeg ?? 0);
  const flipX = def.flipX === true;
  const standLocal = localOffsetToWorld(
    def.stand,
    rotationDeg,
    worldAspect,
    flipX,
  );
  return { x: def.x + standLocal.x, y: def.y + standLocal.y };
}

/** Build the internal floor scenery layer for a composite prop. */
export function placeCompositeFloorLayer(
  def: Pick<
    WorldObjectDef,
    'id' | 'x' | 'y' | 'widthFrac' | 'rotationDeg' | 'flipX' | 'floor'
  >,
  worldAspect: number,
): WorldSceneryLayer | null {
  const floor = def.floor;
  if (!floor) return null;

  const rotationDeg = normalizeRotationDeg(def.rotationDeg ?? 0);
  const flipX = def.flipX === true;
  const offset = floor.offset ?? { x: 0, y: 0 };
  const offsetWorld = localOffsetToWorld(
    offset,
    rotationDeg,
    worldAspect,
    flipX,
  );
  const fx = def.x + offsetWorld.x;
  const fy = def.y + offsetWorld.y;
  const widthFrac = def.widthFrac * (floor.widthScale ?? 1);
  const height =
    floor.heightFrac != null && floor.heightFrac > 0
      ? floor.heightFrac
      : spriteHeightFrac(widthFrac, floor.sprite, worldAspect);

  const hasFreeform = isQuadCorners(floor.quadCorners);
  const quadScale =
    hasFreeform &&
    floor.quadAuthoredWidth != null &&
    floor.quadAuthoredWidth > 0
      ? widthFrac / floor.quadAuthoredWidth
      : 1;
  const localCorners: QuadCorners = hasFreeform
    ? ([
        {
          x: floor.quadCorners![0].x * quadScale,
          y: floor.quadCorners![0].y * quadScale,
        },
        {
          x: floor.quadCorners![1].x * quadScale,
          y: floor.quadCorners![1].y * quadScale,
        },
        {
          x: floor.quadCorners![2].x * quadScale,
          y: floor.quadCorners![2].y * quadScale,
        },
        {
          x: floor.quadCorners![3].x * quadScale,
          y: floor.quadCorners![3].y * quadScale,
        },
      ] as QuadCorners)
    : rectQuadCorners(widthFrac / 2, height / 2);
  const rotatedCorners = rotateQuadCorners(
    localCorners,
    rotationDeg,
    worldAspect,
  );
  const contain = hasFreeform
    ? quadContainSize(rotatedCorners)
    : { width: widthFrac, height };
  // Match groundDecalDepthFromY — keep floors under character / upright props.
  const depth = groundDecalDepthBand(fy);

  return {
    id: `${def.id}-floor`,
    kind: 'ground-decal',
    zIndex: depth,
    depth,
    src: floor.sprite.src,
    alt: floor.sprite.alt,
    x: fx,
    y: fy,
    width: hasFreeform ? Math.max(widthFrac, contain.width) : widthFrac,
    height: hasFreeform ? Math.max(height, contain.height) : height,
    rotationDeg: rotationDeg || undefined,
    ...(flipX ? { flipX: true } : {}),
    ...(hasFreeform
      ? {
          shapeCorners: rotatedCorners,
          shapeCornersLocal: localCorners,
        }
      : {}),
    placeholderClass: `wp-layer-${def.id}-floor`,
    compositeParentId: def.id,
  };
}

/** Same band as groundDecalDepthFromY (kept local to avoid import cycles). */
function groundDecalDepthBand(y: number): number {
  return Math.round(25 + y * 55);
}

/**
 * Expand a WorldObjectDef into the three space wirings
 * (paint layer(s), collision footprint, interactive object).
 */
export function placeObject(
  def: WorldObjectDef,
  options: PlaceObjectOptions,
): PlacedObject {
  if (def.enabled === false) {
    return { sceneryLayers: [], walkObstacle: null, interactiveObject: null };
  }

  const depth = def.depth ?? depthFromY(def.y);
  const rotationDeg = normalizeRotationDeg(def.rotationDeg ?? 0);
  const flipX = def.flipX === true;
  const layers: WorldSceneryLayer[] = [];

  const floorLayer = placeCompositeFloorLayer(def, options.worldAspect);
  if (floorLayer) layers.push(floorLayer);

  const occlusion =
    def.occlusion?.enabled === true ? def.occlusion : undefined;
  const occlusionLocal = occlusion
    ? localOffsetsToWorld(
        sourceRectToPolygon(occlusion.sourceRect),
        rotationDeg,
        options.worldAspect,
        flipX,
      )
    : null;
  const occlusionWorldRect =
    occlusion && occlusionLocal
      ? occlusionRectWorld(
          { x: def.x, y: def.y },
          occlusion.sourceRect,
          occlusionLocal,
        )
      : undefined;

  const sprite = resolveObjectSprite(def);
  if (sprite) {
    const height = spriteHeightFrac(
      def.widthFrac,
      sprite,
      options.worldAspect,
    );
    const gardenType = def.gardenType;
    if (occlusion && occlusionLocal) {
      // Background: full art stays behind the character while walking behind.
      const bgDepth = occlusionBackgroundDepth(depth);
      layers.push({
        id: def.id,
        kind: 'structure',
        zIndex: bgDepth,
        depth: bgDepth,
        src: sprite.src,
        alt: sprite.alt,
        x: def.x,
        y: def.y,
        width: def.widthFrac,
        height,
        placeholderClass: `wp-layer-${def.id}`,
        ambient: def.ambient,
        groundShadow: def.groundShadow,
        rotationDeg: rotationDeg || undefined,
        ...(flipX ? { flipX: true } : {}),
        ...(gardenType ? { gardenType } : {}),
        occlusionRole: 'background',
      });
      // Occlusion: same texture, cropped to the lower band, sorts with character.
      layers.push({
        id: `${def.id}-occlusion`,
        kind: 'structure',
        zIndex: depth,
        depth,
        src: sprite.src,
        alt: sprite.alt,
        x: def.x,
        y: def.y,
        width: def.widthFrac,
        height,
        rotationDeg: rotationDeg || undefined,
        ...(flipX ? { flipX: true } : {}),
        clipPolygon: offsetPolygon(def.x, def.y, occlusionLocal),
        occlusionRole: 'occlusion',
        occlusionRect: occlusionWorldRect,
        compositeParentId: def.id,
      });
    } else {
      layers.push({
        id: def.id,
        kind: 'structure',
        zIndex: depth,
        depth,
        src: sprite.src,
        alt: sprite.alt,
        x: def.x,
        y: def.y,
        width: def.widthFrac,
        height,
        placeholderClass: `wp-layer-${def.id}`,
        ambient: def.ambient,
        groundShadow: def.groundShadow,
        rotationDeg: rotationDeg || undefined,
        ...(flipX ? { flipX: true } : {}),
        ...(gardenType ? { gardenType } : {}),
      });
    }
  }

  if (occlusion && occlusionLocal && !def.sprite) {
    // Baked into the master plate — re-paint only the occlusion crop in front.
    if (!options.masterSrc) {
      throw new Error(
        `placeObject(${def.id}): occlusion on a baked prop requires options.masterSrc`,
      );
    }
    // Sort just behind the ground anchor so a character standing at the
    // front corner (same Y as the shed) stays in front of the crop.
    const occlusionDepth = Math.max(25, depth - 2);
    layers.push({
      id: `${def.id}-occlusion`,
      kind: 'overlay',
      zIndex: occlusionDepth,
      depth: occlusionDepth,
      src: options.masterSrc,
      clipPolygon: offsetPolygon(def.x, def.y, occlusionLocal),
      occlusionRole: 'occlusion',
      occlusionRect: occlusionWorldRect,
    });
  } else if (
    !occlusion &&
    def.occluderClip &&
    def.occluderClip.length >= 3
  ) {
    // Legacy full-silhouette clip (prefer `occlusion` for new props).
    if (!options.masterSrc) {
      throw new Error(
        `placeObject(${def.id}): occluderClip requires options.masterSrc`,
      );
    }
    const clipLocal = localOffsetsToWorld(
      def.occluderClip,
      rotationDeg,
      options.worldAspect,
      flipX,
    );
    layers.push({
      id: `${def.id}-occluder`,
      kind: 'overlay',
      zIndex: depth,
      depth,
      src: options.masterSrc,
      clipPolygon: offsetPolygon(def.x, def.y, clipLocal),
      occlusionRole: 'occlusion',
    });
  }

  const hitLocal = localOffsetToWorld(
    def.hit ??
      (sprite
        ? defaultSpriteHitOffset(def.widthFrac, sprite, options.worldAspect)
        : { x: 0, y: 0 }),
    rotationDeg,
    options.worldAspect,
    flipX,
  );
  const standLocal = localOffsetToWorld(
    def.stand,
    rotationDeg,
    options.worldAspect,
    flipX,
  );
  const zoneLocal = def.activationZone
    ? localOffsetToWorld(
        { x: def.activationZone.x, y: def.activationZone.y },
        rotationDeg,
        options.worldAspect,
        flipX,
      )
    : { x: 0, y: 0 };
  const interactive =
    def.interactive !== false
      ? ({
          id: def.id,
          title: def.title,
          icon: def.icon,
          actionLabel: def.actionLabel,
          sceneryId: layers[0]?.id ?? undefined,
          x: def.x + hitLocal.x,
          y: def.y + hitLocal.y,
          standX: def.x + standLocal.x,
          standY: def.y + standLocal.y,
          hitRadius: def.hitRadius,
          activationZone: placeActivationZone(
            def.activationZone,
            { x: def.x, y: def.y },
            zoneLocal,
          ),
          shape: def.shape,
        } satisfies InteractiveObject)
      : null;

  const footprintLocal = localOffsetsToWorld(
    def.footprint,
    rotationDeg,
    options.worldAspect,
    flipX,
  );
  const walkObstacle =
    footprintLocal.length >= 3
      ? {
          id: def.id,
          polygon: offsetPolygon(def.x, def.y, footprintLocal),
        }
      : null;

  return {
    sceneryLayers: layers,
    walkObstacle,
    interactiveObject: interactive,
  };
}

/** Place many defs; filters disabled / empty results for easy space wiring. */
export function placeObjects(
  defs: readonly WorldObjectDef[],
  options: PlaceObjectOptions,
): {
  sceneryLayers: WorldSceneryLayer[];
  walkObstacles: WalkObstacle[];
  interactiveObjects: InteractiveObject[];
} {
  const sceneryLayers: WorldSceneryLayer[] = [];
  const walkObstacles: WalkObstacle[] = [];
  const interactiveObjects: InteractiveObject[] = [];
  for (const def of defs) {
    const placed = placeObject(def, options);
    sceneryLayers.push(...placed.sceneryLayers);
    if (placed.walkObstacle) walkObstacles.push(placed.walkObstacle);
    if (placed.interactiveObject) {
      interactiveObjects.push(placed.interactiveObject);
    }
  }
  return { sceneryLayers, walkObstacles, interactiveObjects };
}
